// src/pages/purchases/AddPurchase.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';
import API_BASE_URL from '../../utils/config';

const selBase = {
  control: (base) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    boxShadow: 'none',
    borderColor: '#e5e7eb',
  }),
  menu: (base) => ({ ...base, zIndex: 40 }),
};

const toInputDate = (v) => {
  if (!v) return '';
  // accept YYYY-MM-DD or DD-MM-YYYY or YYYY/MM/DD
  const s = String(v).trim();
  // DD-MM-YYYY -> YYYY-MM-DD
  const m1 = /^(\d{2})[-\/](\d{2})[-\/](\d{4})$/.exec(s);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  // YYYY-MM-DD acceptable
  const m2 = /^(\d{4})[-\/](\d{2})[-\/](\d{2})$/.exec(s);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  try {
    const d = new Date(s);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
};

const fromInputDate = (v) => (v ? String(v) : ''); // already yyyy-mm-dd from <input type="date">

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const isNum = (n) => Number.isFinite(Number(n));

export default function AddPurchase() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const [header, setHeader] = useState({
    supplier_name: '',
    invoice_number: '',
    invoice_date: toInputDate(new Date().toISOString().slice(0, 10)),
  });

  const [items, setItems] = useState([
    {
      // selections
      productOption: null, // {value,label}
      product_name: '', // for new product
      category_name: '',
      hsn_code: '',
     gst_percent: '',
     pack: '',

      // batch flow
      batchMode: 'existing', // 'existing' | 'new'
      batchOptions: [], // options for existing batches
      batchOption: null, // selected batch from options
      // free fields (used when batchMode === 'new' OR no batch selected)
      batch_number: '',
      expiry_date: '',
      quantity: '',
      mrp: '',
      rate: '',

      // meta
      productId: null,
      batchLocked: false, // true when existing batch selected -> lock fields
      loadingBatches: false,
    },
  ]);

  const fetchCategories = async (q = '') => {
  const res = await axios.get(`${API_BASE_URL}/api/categories`, {
    params: { q, limit: 25 },
    headers: authHeader,
  });
  const arr = Array.isArray(res.data) ? res.data : res.data?.rows || [];
  return arr.map((c) => ({ value: c.name, label: c.name })); // we store category_name string
};

  // ---------- lookups ----------
  const fetchProducts = async (q = "") => {
   const res = await axios.get(`${API_BASE_URL}/api/products`, {

      params: { q, limit: 20 },
      headers: authHeader,
    });
    const arr = Array.isArray(res.data) ? res.data : res.data?.rows || [];
    return arr.map((p) => ({
      value: p.id,
      label: p.name,
      category_name: p.category_name || '',
      hsn_code: p.hsn_code || p.hsn || '',
     gst_percent: (p.gst_percent ?? p.tax_percent ?? ''),
     pack: p.pack || '',
     mrp : p.mrp,
     rate: p.purchase_rate ?? p.rate ?? '', // op
    }));
  };

  const fetchBatches = async (productId) => {
    const res = await axios.get(`${API_BASE_URL}/api/products/${productId}/batches`, {
      params: { limit: 50 },
      headers: authHeader,
    });
    const arr = Array.isArray(res.data) ? res.data : res.data?.rows || [];
    return arr.map((b) => ({
      value: b.id,
      label: b.batch_no || b.batch_number || `#${b.id}`,
      batch_no: b.batch_no || b.batch_number,
      expiry_date: b.expiry_date || '',
      mrp: b.mrp ?? '',
      rate: b.rate ?? b.rates ?? '',
    }));
  };

  // ---------- handlers ----------
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((h) => ({ ...h, [name]: value }));
  };

  const setItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const onCreateProduct = (idx, input) => {
    setItem(idx, {
      productOption: { value: null, label: input },
      product_name: input,
      category_name: '',
       hsn_code: '',
     gst_percent: '',
     pack: '',
      productId: null,
      batchMode: 'new',
      batchOptions: [],
      batchOption: null,
      batch_number: '',
      expiry_date: '',
      mrp: '',
      rate: '',
      batchLocked: false,
    });
  };

  const onSelectProduct = async (idx, opt) => {
    // selected existing
    const category_name = opt?.category_name || '';
    setItem(idx, {
      productOption: opt,
      product_name: opt?.label || '',
      category_name,
      hsn_code: opt?.hsn_code || '',
     gst_percent: opt?.gst_percent ?? '',
     pack: opt?.pack || '',
      productId: opt?.value || null,
      batchMode: 'existing',
      batchOption: null,
      batchOptions: [],
      batch_number: '',
      expiry_date: '',
      mrp: opt.mrp ?? '',
      rate: opt.rate ?? '',
      batchLocked: false,
      loadingBatches: true,
    });

    // load batches
    try {
       const options = await fetchBatches(opt.value);
       setItem(idx, { batchOptions: options });
    } catch (e) {
      console.error('fetchBatches error', e);
      setItem(idx, { batchOptions: [{ value: '__NEW__', label: '➕ New batch…' }] });
    } finally {
      setItem(idx, { loadingBatches: false });
    }
  };

  const onSelectBatch = (idx, opt) => {
    if (!opt) {
      setItem(idx, {
        batchOption: null,
        batchLocked: false,
      });
      return;
    }
    if (opt.value === '__NEW__') {
      // switch to new batch mode
      setItem(idx, {
        batchMode: 'new',
        batchOption: null,
        batchLocked: false,
        batch_number: '',
        expiry_date: '',
        mrp: '',
      });
      return;
    }
    // existing batch selected -> lock fields and autofill
    setItem(idx, {
      batchMode: 'existing',
      batchOption: opt,
      batchLocked: true,
      batch_number: opt.batch_no || opt.label,
      expiry_date: toInputDate(opt.expiry_date),
      mrp: opt.mrp ?? '',
    });
  };

  const onFieldChange = (idx, e) => {
    const { name, value } = e.target;
    setItem(idx, { [name]: value });
  };

  const addItemRow = () =>
    setItems((prev) => [
      ...prev,
      {
        productOption: null,
        product_name: '',
        category_name: '',
          hsn_code: '',
       gst_percent: '',
       pack: '',
        batchMode: 'existing',
        batchOptions: [],
        batchOption: null,
        batch_number: '',
        expiry_date: '',
        quantity: '',
        rate: '', 
        mrp: '',
        productId: null,
        batchLocked: false,
        loadingBatches: false,
      },
    ]);

  const removeItemRow = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  // ---------- submit ----------
  const payload = useMemo(() => {
    // compute total
    const total = items.reduce((s, it) => {
      const qty = Number(it.quantity) || 0;
  const rate = Number(it.rate) || 0;
  const gst = it.gst_percent ? Number(it.gst_percent) : 0;
  return s + qty * rate * (1 + gst / 100);
    }, 0);

    // shape for API
    const outItems = items.map((it) => ({
      product_name: it.productOption?.label || it.product_name || '',
      category_name: it.category_name || '',
      hsn_code: it.hsn_code || '',
     gst_percent: it.gst_percent === '' ? null : Number(it.gst_percent),
     pack: it.pack || '',
      batch_number:
        it.batchMode === 'existing'
          ? (it.batchOption?.batch_no || it.batchOption?.label || it.batch_number || '')
          : it.batch_number,
      expiry_date: fromInputDate(it.expiry_date),
      quantity: it.quantity ? Number(it.quantity) : 0,
      purchase_rate: it.rate ? Number(it.rate) : 0,  // NEW
      mrp: it.mrp ? Number(it.mrp) : 0,      
     }));

    return {
      supplier_name: header.supplier_name,
      invoice_number: header.invoice_number,
      invoice_date: header.invoice_date,
      total_amount: round2(total),
      items: outItems,
    };
  }, [header, items]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/purchases`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Purchase saved');
      navigate('/purchases');
    } catch (err) {
      console.error('Purchase submit error:', err);
      alert(err?.response?.data?.error || 'Failed to save purchase');
    }
  };

  // ---------- UI ----------
  const amount = payload.total_amount;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Add Purchase</h2>
      <form onSubmit={onSubmit} className="bg-white p-6 rounded shadow space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <input
            name="supplier_name"
            value={header.supplier_name}
            onChange={handleHeaderChange}
            placeholder="Supplier Name"
            className="w-full border p-2 rounded"
            required
          />
          <input
            name="invoice_number"
            value={header.invoice_number}
            onChange={handleHeaderChange}
            placeholder="Invoice Number"
            className="w-full border p-2 rounded"
            required
          />
          <input
            type="date"
            name="invoice_date"
            value={header.invoice_date}
            onChange={handleHeaderChange}
            className="w-full border p-2 rounded"
            required
          />
        </div>

        {items.map((it, idx) => (
          <div key={idx} className="border p-3 rounded bg-gray-50 space-y-3">
            {/* Product row */}
            <div className="grid md:grid-cols-9 gap-2 items-center">
              <div className="md:col-span-2">
                <div className="text-xs text-gray-500 mb-1">Product</div>
                <AsyncCreatableSelect
                  cacheOptions
                  defaultOptions
                  value={it.productOption}
                  loadOptions={fetchProducts}
                  onChange={(opt) => onSelectProduct(idx, opt)}
                  onCreateOption={(input) => onCreateProduct(idx, input)}
                  isClearable
                  placeholder="Search or create product…"
                  classNamePrefix="prod"
                  styles={selBase}
                />
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Category</div>
                <AsyncCreatableSelect
                cacheOptions
                defaultOptions
                loadOptions={fetchCategories}
                value={it.category_name ? { value: it.category_name, label: it.category_name } : null}
                onChange={(opt) => setItem(idx, { category_name: opt?.label || '' })}
                onCreateOption={(input) => setItem(idx, { category_name: input })}
                isClearable
                placeholder="Select or create category…"
                classNamePrefix="cat"
                styles={selBase}
              />
              </div>

              <div className="md:col-span-3">
                <div className="text-xs text-gray-500 mb-1">Batch</div>
                 {it.batchMode === 'existing' ? (
                    <CreatableSelect
                      isLoading={it.loadingBatches}
                      options={it.batchOptions}
                      value={it.batchOption}
                      onChange={(opt) => onSelectBatch(idx, opt)}
                      onCreateOption={(input) =>
                        setItem(idx, {
                          batchMode: 'new',
                          batchOption: null,
                          batchLocked: false,
                          batch_number: input,   // prefill the typed batch
                          expiry_date: '',
                          mrp: '',
                        })
                      }
                      isClearable
                      placeholder={it.productId ? 'Search/select batch or type to add…' : 'Select a product first'}
                      classNamePrefix="batch"
                      styles={selBase}
                      isDisabled={!it.productId}
                    />
                  ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      name="batch_number"
                      value={it.batch_number}
                      onChange={(e) => onFieldChange(idx, e)}
                      placeholder="Batch No"
                      className="w-full border p-2 rounded"
                    />
                    <input
                      type="date"
                      name="expiry_date"
                      value={it.expiry_date}
                      onChange={(e) => onFieldChange(idx, e)}
                      className="w-full border p-2 rounded"
                    />
                    <button
                      type="button"
                      onClick={() => setItem(idx, { batchMode: 'existing', batchOption: null, batchLocked: false })}
                      className="border rounded px-3 py-2"
                    >
                      Use existing…
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Details row */}
            <div className="grid md:grid-cols-6 gap-2 items-center">
              <div>
                <div className="text-xs text-gray-500 mb-1">Batch No</div>
                <input
                  name="batch_number"
                  value={it.batch_number}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="Batch No"
                  className={`w-full border p-2 rounded ${it.batchLocked ? 'bg-gray-100' : ''}`}
                  disabled={it.batchLocked}
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Expiry</div>
                <input
                  type="date"
                  name="expiry_date"
                  value={it.expiry_date}
                  onChange={(e) => onFieldChange(idx, e)}
                  className={`w-full border p-2 rounded ${it.batchLocked ? 'bg-gray-100' : ''}`}
                  disabled={it.batchLocked}
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Quantity</div>
                <input
                  type="number"
                  name="quantity"
                  value={it.quantity}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="Qty"
                  className="w-full border p-2 rounded"
                  min="0"
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">MRP</div>
                <input
                  type="number"
                  name="mrp"
                  value={it.mrp}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="MRP"
                  className={`w-full border p-2 rounded ${it.batchLocked ? 'bg-gray-100' : ''}`}
                  disabled={it.batchLocked}
                  step="0.01"
                />
              </div>
              <div>
              <div className="text-xs text-gray-500 mb-1">Rate</div>
              <input
                type="number"
                name="rate"
                value={it.rate}
                onChange={(e) => onFieldChange(idx, e)}
                placeholder="Rate"
                className="w-full border p-2 rounded"
                step="0.01"
                min="0"
              />
            </div>
            <div>
               <div className="text-xs text-gray-500 mb-1">HSN</div>
                <input
                  name="hsn_code"
                  value={it.hsn_code}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="HSN"
                  className="w-full border p-2 rounded"
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">GST %</div>
                <input
                  type="number"
                  name="gst_percent"
                  value={it.gst_percent}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="%"
                  className="w-full border p-2 rounded"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Pack</div>
                <input
                  name="pack"
                  value={it.pack}
                  onChange={(e) => onFieldChange(idx, e)}
                  placeholder="e.g. 10x10"
                  className="w-full border p-2 rounded"
                />
              </div>
              <div className="text-right md:self-end">
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItemRow(idx)}
                    className="text-red-600 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        <div className="flex items-center justify-between">
          <button type="button" onClick={addItemRow} className="text-blue-600 text-sm">
            + Add More Items
          </button>
          <div className="text-sm text-gray-700">
            <span className="mr-2">Total:</span>
            <span className="font-semibold">₹ {amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">
          Save Purchase
        </button>
      </form>
    </div>
  );
}
